import 't-lib-postviewer/css/postviewer.css';
import 't-lib-postviewer/css/post-tailwind.css';
import 't-lib-postviewer/css/post-tailwind-responsive.css';
import './PostView.css';
import ArrowRightIcon from './ArrowRightIcon';
import ShowAllIcon from './ShowAllIcon';
import UndoIcon from './UndoIcon';
import HamburgerIcon from './HamburgerIcon';
import HintIcon from './HintIcon';
import PrivateFeedIcon from './PrivateFeedIcon';
import MailIcon from './MailIcon';
import FeedIssueIcon from './FeedIssueIcon';
import FeedIssuePopup from './FeedIssuePopup';
import { hasFeedIssue } from '../utils/feedIssue';
import AllReadIllustration from './AllReadIllustration';
import PostRenderer from './PostRenderer';
import type { PostRendererHandle } from './PostRenderer';
import PostViewHeader from './PostViewHeader';
import NewsletterInfoPopup from './NewsletterInfoPopup';
import FeedIcon from './FeedIcon';
import { getSinglePost } from '../connector/post-engine';
import { getMode } from '../connector/auth';
import { MAX_ANON_SAVED_POSTS } from '../connector/feed-engine';
import { MAX_SAVED_POSTS } from '../schema-copy/api-schema';
import { usePreferences } from '../connector/preferences';
import { useState, useEffect, useRef, useCallback, useContext, useMemo, forwardRef, useImperativeHandle, useLayoutEffect, type MouseEvent as ReactMouseEvent } from 'react';
import { FocusText, Spinner, TextButton } from 't-lib-ui/widgets';
import { HelpPopup, useProgressModal, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { AppState } from '../context/general';
import { ShowMeWhereGuide } from './ShowMeWhere';
import { usePostViewFocus } from './PostViewFocus';
import SpecialListArt from './SpecialListArt';
import { useShare } from './SharePopup';
import { useReport } from './ReportPopup';
import { useLimitUnread } from './LimitUnreadPopup';
import { usePauseFeed } from './PauseFeedPopup';
import { getPlanLimits } from '../schema-copy/userPlans';
import { usePostAppearance } from './PostAppearancePopup';
import ContextMenu from './ContextMenu';
import type { ContextMenuItem } from './ContextMenu';
import PlusIcon from './PlusIcon';
import ChevronLeftIcon from './ChevronLeftIcon';
import ListIcon from './ListIcon';
import SingleColumnIcon from './SingleColumnIcon';
import FlagIcon from './FlagIcon';
import { menuKeyDown, menuBlur } from './menuUtils';
import './PageHeader.css';
import PostViewNavButton from './PostViewNavButton';
import ScrollNavBtn from './ScrollNavBtn';
import { GlobalFooterContent, isGlobalFooterVisible } from './ThreeColumnLayout';
import { hasUnreadInScope } from './unreadScope';
import { RouterContext, goTo } from 't-lib-ui/router';

interface PostViewProps {
  posts: [string, number][];
  feedName?: string;
  feedFaviconUrl?: string;
  feedCircularIcon?: boolean;
  isPrivateFeed?: boolean;
  isNewsletterFeed?: boolean;
  newsletterEmail?: string;
  // Repeated-fetch-failure info for the current feed; drives the "feed has a problem" flag.
  feedFetchErrorCount?: number;
  feedFailingSinceAt?: string;
  feedFeedStatus?: string;
  showExpandButton?: boolean;
  onExpand?: () => void;
  isMobile?: boolean;
  currentPostIndex?: number;
  onNextPost?: () => void;
  onPrevPost?: () => void;
  onToggleStar?: (feedId: string, postNumber: number, isStarred: boolean) => void;
  onToggleReadLater?: (feedId: string, postNumber: number, isReadLater: boolean) => void;
  onToggleRead?: (feedId: string, postNumber: number, isRead: boolean) => void;
  onMarkAllRead?: () => void;
  // Restores a previously-displayed post list (used to undo "Mark all read", which in
  // unread-only mode empties the list to the "All caught up" screen).
  onRestorePostList?: (posts: [string, number][]) => void;
  onKeepAllUnread?: () => void;
  onMarkOlderThanRead?: (days: number) => void;
  sortNewestFirst?: boolean;
  onSortChange?: (newestFirst: boolean) => void;
  showUnreadOnly?: boolean;
  showRead?: 'u' | 'a';
  onSetShowRead?: (showRead: 'u' | 'a') => void;
  onEndOfUnread?: () => void;
  onShowAll?: () => void;
  onRefresh?: () => void;
  // The end-of-list navigation target: a feed, or (in the folder unread view) the
  // next folder with unread posts (id 'folder:<name>', isFolder set).
  nextUnreadFeed?: { id: string; name: string; isFolder?: boolean };
  // Folder unread view (/a/new/<folder>): limits "new unread posts have arrived" to
  // the folder's feeds — unread posts elsewhere mean "caught up in this folder".
  unreadFolder?: string;
  onNavigateToFeed?: (feedId: string) => void;
  scrollToPost?: [string, number];
  onVisiblePostChange?: (feedId: string, postNumber: number) => void;
  viewMode?: 'full' | 'single' | 'list';
  onCycleViewMode?: () => void;
  onSetViewMode?: (mode: 'full' | 'single' | 'list') => void;
  feedConfigKey?: string;
  // 'preview': an unfollowed feed's posts, read-only (no read marking, no unread count) with a
  // minimal header; `listHeader` (its FeedTile) scrolls above the posts.
  specialListType?: 'starred' | 'read-later' | 'all-items' | 'preview';
  listHeader?: React.ReactNode;
  // Click on the feed name in the header: the parent toggles the feed's FeedTile as `listHeader`;
  // when it appears, PostView scrolls up to it.
  onFeedNameClick?: () => void;
  // Shows the FeedTile (unlike onFeedNameClick, which toggles it); the menus' "Feed setup…".
  onShowFeedTile?: () => void;
  // Back arrow in the 'preview' header.
  onBack?: () => void;
  // The 'preview' header menu's entries for the feed you don't follow: follow it, report it.
  onFollow?: () => void;
  previewFeedId?: string;
  loading?: boolean;
  feedLoadingPosts?: boolean;
  hasMoreUnread?: boolean;
  onMarkAllAndLoadMore?: () => void;
  onLoadMore?: () => void;
  loadMoreLabel?: string;
  loadMoreWithNextFeed?: boolean;
  overflowMessage?: string;
  onUnfollow?: () => void;
  feedCategory?: string;
  isNavigatingAway?: boolean;
}

export interface PostViewHandle {
  updatePostStatus: (feedId: string, postIndex: number, type: 'star' | 'readLater' | 'read', value: boolean) => void;
  keepAllUnread: () => void;
  listNavigate: (direction: 1 | -1) => boolean;
  markAllReadUnlessLocked: () => void;
  prevPostOrScrollToTop: (thresholdPx: number) => boolean;
  nextFromTopContent: (thresholdPx: number) => boolean;
  getCurrentVisible: () => [string, number] | null;
  toggleKeepUnread: (feedId: string, postIndex: number) => void;
  hasLockedPosts: () => boolean;
  /** Animates the post container to its very bottom through the renderer's own scroll
   *  machine (not a native smooth scroll). Returns false when no renderer is mounted. */
  scrollToBottom: () => boolean;
  /** Shows the "saved-posts limit reached" toast (anon cap refusals). */
  showSavedLimitToast: (kind: 'star' | 'readLater') => void;
}

let lastMeasuredPostViewWidth = 0;
let lastGutterHoverSide: 'left' | 'right' | null = null;

const PostView = forwardRef<PostViewHandle, PostViewProps>(({ posts, feedName, feedFaviconUrl, feedCircularIcon, isPrivateFeed, isNewsletterFeed, newsletterEmail, feedFetchErrorCount, feedFailingSinceAt, feedFeedStatus, showExpandButton, onExpand, isMobile, currentPostIndex = 0, onNextPost, onPrevPost, onToggleStar, onToggleReadLater, onToggleRead, onMarkAllRead, onRestorePostList, onKeepAllUnread, onMarkOlderThanRead, sortNewestFirst = true, onSortChange, showUnreadOnly, showRead = 'u', onSetShowRead, onEndOfUnread, onShowAll, onRefresh, nextUnreadFeed, unreadFolder, onNavigateToFeed, scrollToPost, onVisiblePostChange, viewMode = 'full', onCycleViewMode, onSetViewMode, feedConfigKey, specialListType, loading, feedLoadingPosts, hasMoreUnread, onMarkAllAndLoadMore, onLoadMore, loadMoreLabel, loadMoreWithNextFeed, overflowMessage: overflowMessageProp, onUnfollow, feedCategory, isNavigatingAway, listHeader, onFeedNameClick, onShowFeedTile, onBack, onFollow, previewFeedId }, ref) => {
  const [showPrivateInfo, setShowPrivateInfo] = useState(false);
  const [showNewsletterInfo, setShowNewsletterInfo] = useState(false);
  const [showIssueInfo, setShowIssueInfo] = useState(false);
  const [copiedNewsletterEmail, setCopiedNewsletterEmail] = useState(false);
  const copyNewsletterEmail = async () => {
    if (!newsletterEmail) return;
    try {
      await navigator.clipboard.writeText(newsletterEmail);
      setCopiedNewsletterEmail(true);
      setTimeout(() => setCopiedNewsletterEmail(false), 2000);
    } catch { /* clipboard unavailable — address is still shown */ }
  };
  const appState = useContext(AppState);
  const feedHasIssue = hasFeedIssue({ feedStatus: feedFeedStatus, fetchErrorCount: feedFetchErrorCount, fetchFailingSinceAt: feedFailingSinceAt });
  // Server-confirmed "the sync server is fetching this feed right now" (set by the subscribe
  // response) — shows a small spinner next to the feed name, also for feeds that already have posts.
  const feedRefreshing = !!feedConfigKey && appState.feedEngine.isRefreshing(feedConfigKey);
  const statusBadge = isNewsletterFeed
    ? <span id="pvNlBadge" tabIndex={0} className="privateLockButton" onClick={e => { e.stopPropagation(); setShowNewsletterInfo(true); }} title="Newsletter feed" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><MailIcon size={14} color="currentColor" /></span>
    : isPrivateFeed
    ? <span className="privateLockButton" onClick={() => setShowPrivateInfo(true)} title="Private feed" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><PrivateFeedIcon size={14} color="currentColor" /></span>
    : null;
  // Rendered in the same slot as the private/newsletter badge, so the issue flag and the
  // refreshing spinner appear next to the feed name in every header variant without threading
  // a new header prop.
  const privateLockIcon = (statusBadge || feedHasIssue || feedRefreshing) ? (
    <>
      {statusBadge}
      {feedHasIssue && <span id="pvIssueBadge" tabIndex={0} className="privateLockButton" onClick={e => { e.stopPropagation(); setShowIssueInfo(true); }} title="This feed has a problem" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><FeedIssueIcon size={14} color="var(--error-color)" /></span>}
      {feedRefreshing && <span id="pvRefreshingBadge" className="privateLockButton" title="Checking the feed for new posts" style={{ display: 'inline-flex', alignItems: 'center' }}><Spinner spinning className="feedRefreshingSpinner" /></span>}
    </>
  ) : null;
  const [, updatePreferences] = usePreferences();
  const routerCtx = useContext(RouterContext);
  const canFilterPosts = getPlanLimits(appState.state.currentUser?.userPlan ?? 'free').keywordFilters;
  const postViewRef = useRef<HTMLDivElement>(null);
  usePostViewFocus(postViewRef);
  // When the tile appears, scroll up after the click's re-render, which attaches it above the first post.
  const feedNameClickHandler = feedConfigKey && onFeedNameClick ? (ev: ReactMouseEvent) => {
    ev.stopPropagation();
    onFeedNameClick();
    if (!listHeader) requestAnimationFrame(() => rendererRef.current?.moveToTop(false));
  } : undefined;
  const feedSetupHandler = feedConfigKey && !specialListType && onShowFeedTile ? () => {
    onShowFeedTile();
    requestAnimationFrame(() => rendererRef.current?.moveToTop(false));
  } : undefined;
  const feedNameClass = feedNameClickHandler ? 'feedNameText clickableFeedNameText' : 'feedNameText';

  // Header-left for the empty/loading states. Mobile and narrow ("medium") both render a
  // hamburger so the overlay sidebar can be reopened (otherwise there's no way back —
  // these states don't use the full PostViewHeader). Desktop just shows the title.
  // A single feed's header always shows an icon. Until its URL is known (e.g. a preview whose feed
  // info is still loading) or when it fails to load, a same-sized placeholder keeps the name from
  // shifting once the icon arrives.
  const iconPlaceholder = !specialListType || specialListType == 'preview' ? <span className="feedIcon feedIconPlaceholder" /> : null;
  const feedIcon = feedFaviconUrl
    ? <FeedIcon className="feedIcon" src={feedFaviconUrl} size={16} circular={feedCircularIcon} fallback={iconPlaceholder} />
    : iconPlaceholder;

  const renderEmptyStateHeaderLeft = (displayName: string | undefined, withLock: boolean) => {
    const heading = <h2><span className={feedNameClass} onClick={feedNameClickHandler}>{displayName}</span>{withLock && privateLockIcon}</h2>;
    const icon = feedIcon;
    if (isMobile)
      return <div className="mobileHeaderLeft flowFocussable" tabIndex={0} onClick={onExpand}><HamburgerIcon size={24} className="headerHamburger" />{icon}{heading}</div>;
    if (showExpandButton && onExpand)
      return <div className="mediumHeaderLeft flowFocussable" tabIndex={0} onClick={onExpand} onKeyDown={ev => { if (ev.key == 'Enter') { ev.preventDefault(); ev.stopPropagation(); onExpand?.(); } }}><HamburgerIcon size={22} className="headerHamburger" />{icon}{heading}</div>;
    return <h2>{icon}<span className={feedNameClass} onClick={feedNameClickHandler}>{displayName}</span>{withLock && privateLockIcon}</h2>;
  };

  const [showMarkReadDropdown, setShowMarkReadDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showHintToast, setShowHintToast] = useState(false);
  const hintToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const optionsMenuButtonRef = useRef<HTMLButtonElement>(null);
  const [allMarkedRead, setAllMarkedRead] = useState(false);
  const [firstPostImageOnly, setFirstPostImageOnly] = useState(false);
  const [hintGuide, setHintGuide] = useState<{ x: number; y: number; dx: number; dy: number; nonce: number; phase: 'travel' | 'click' } | null>(null);
  const showMeHintRef = useRef<HTMLSpanElement>(null);
  const [showAllTemporarily, setShowAllTemporarily] = useState(false);
  const [unreadVersion, setUnreadVersion] = useState(0);
  const [savedVersion, setSavedVersion] = useState(0);
  const rendererRef = useRef<PostRendererHandle>(null);
  const { share, sharePopup } = useShare();
  const { report, reportPopup } = useReport();
  const { openLimitUnread, limitUnreadPopup } = useLimitUnread();
  const { openPauseFeed, pauseFeedPopup } = usePauseFeed();
  const { openPostAppearance, postAppearancePopup } = usePostAppearance(isMobile);

  // Esc dismisses the visible hint/setup card, with the same effect as its × button.
  // Visibility conditions mirror the card render guards below; one card closes per press.
  // Priority sits above the layout's Esc-collapses-sidebar handler but below modals
  // (priority 1000+), so an open dialog still gets Esc first.
  const dismissed = appState.state.userPreferences.dismissed;
  const showImageHintCard = firstPostImageOnly && !dismissed?.includes('x');
  useEffect(() => {
    if (!showImageHintCard) return;
    registerKeyHandler('postViewCards', {
      'Escape': () => {
        const prefs = appState.state.userPreferences;
        if (showImageHintCard) { setFirstPostImageOnly(false); updatePreferences({ dismissed: (prefs.dismissed || '') + 'x' }); return true; }
        return false;
      },
    }, false, 20);
    return () => unregisterKeyHandler('postViewCards');
  }, [showImageHintCard, appState.state.userPreferences, updatePreferences]);
  const [shareMenu, setShareMenu] = useState<{ feedId: string; postIndex: number; postTitle: string; originalLink: string; feedUrl: string; position: { x: number; y: number } } | null>(null);
  const [postViewWidth, setPostViewWidth] = useState(() => lastMeasuredPostViewWidth);
  // Mirrors the bottom Keep-All button's "all locked" state for the header's lock toggle.
  const [allLocked, setAllLocked] = useState(false);
  // True when every displayed post is read or locked — nothing left to mark read, so the
  // header offers "Next Feed" instead of "Mark all read".
  const [allReadOrLocked, setAllReadOrLocked] = useState(false);
  // Undo for the bulk actions (Keep All / Mark All Read). Only one is ever active: the
  // last one performed. 'keepAll' delegates to PostRenderer (it holds the lock snapshot);
  // 'markAllRead' is restored here (re-mark unread + bring the post list back). Any
  // individual read/lock change, or a feed/sort/view change, clears it.
  const [undoKind, setUndoKind] = useState<'markAllRead' | 'keepAll' | null>(null);
  const markAllReadUndoRef = useRef<{ posts: { feedId: string; postIndex: number; wasRead: boolean }[]; prevPostList: [string, number][] } | null>(null);
  // Set while a bulk op (or its undo) runs so the per-post onMark calls it makes don't
  // immediately invalidate the undo we're about to record.
  const suppressUndoClearRef = useRef(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  // Keyboard/nav focus sits on the end-of-feed buttons (Show all / Keep All / Next Feed). Only
  // cleared when focus moves to another element: pressing in the gutter blurs to the body, and
  // that must not end hold mode.
  const [endActionFocused, setEndActionFocused] = useState(false);
  const handleEndActionFocus = useCallback((e: React.FocusEvent) =>
    setEndActionFocused(!!(e.target as HTMLElement).closest('.endOfUnreadActions')), []);
  const handleEndActionBlur = useCallback((e: React.FocusEvent) => {
    const next = e.relatedTarget as HTMLElement | null;
    if (next && !next.closest('.endOfUnreadActions')) setEndActionFocused(false);
  }, []);
  useEffect(() => setEndActionFocused(false), [feedConfigKey]);
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const [showScrollNav, setShowScrollNav] = useState(false);
  const [scrollNavVisible, setScrollNavVisible] = useState((appState.state.userPreferences.postNav ?? 'autohide') != 'autohide');
  const scrollNavShownRef = useRef(false);
  const [presentMarkProgress, dismissMarkProgress] = useProgressModal();

  // Anon saved-posts cap: a star/read-later save was refused; explain why in a transient toast.
  const [savedLimitToast, setSavedLimitToast] = useState<'star' | 'readLater' | null>(null);
  const savedLimitToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showSavedLimitToast = useCallback((kind: 'star' | 'readLater') => {
    setSavedLimitToast(kind);
    if (savedLimitToastTimerRef.current) clearTimeout(savedLimitToastTimerRef.current);
    savedLimitToastTimerRef.current = setTimeout(() => {
      setSavedLimitToast(null);
      savedLimitToastTimerRef.current = null;
    }, 6000);
  }, []);
  useEffect(() => () => {
    if (savedLimitToastTimerRef.current) clearTimeout(savedLimitToastTimerRef.current);
  }, []);

  useImperativeHandle(ref, () => ({
    updatePostStatus: (feedId, postIndex, type, value) => rendererRef.current?.updatePostStatus(feedId, postIndex, type, value),
    keepAllUnread: () => rendererRef.current?.keepAllUnread(),
    listNavigate: (direction) => rendererRef.current?.listNavigate(direction) ?? false,
    markAllReadUnlessLocked: () => rendererRef.current?.markAllReadUnlessLocked(),
    prevPostOrScrollToTop: (thresholdPx) => rendererRef.current?.prevPostOrScrollToTop(thresholdPx) ?? false,
    nextFromTopContent: (thresholdPx) => rendererRef.current?.nextFromTopContent(thresholdPx) ?? false,
    getCurrentVisible: () => rendererRef.current?.getCurrentVisible() ?? null,
    toggleKeepUnread: (feedId, postIndex) => rendererRef.current?.toggleKeepUnread(feedId, postIndex),
    hasLockedPosts: () => rendererRef.current?.hasLockedPosts() ?? false,
    scrollToBottom: () => {
      if (!rendererRef.current) return false;
      rendererRef.current.scrollToBottom();
      return true;
    },
    showSavedLimitToast,
  }), [showSavedLimitToast]);

  // Mark-all-as-read click handler. When any post is locked (keepUnread), skip
  // the bulk server path — iterate entries client-side and emit a per-post
  // markPostRead for each non-locked post (queueMarkerChange merges them into
  // a single sync patch anyway).
  const handleMarkAllReadClick = useCallback(() => {
    // Snapshot the read state of every displayed post so the action can be undone.
    const snapshot = posts.map(([feedId, postIndex]) => ({
      feedId, postIndex, wasRead: appState.feedEngine.isPostRead(feedId, postIndex),
    }));
    const prevPostList = posts;
    suppressUndoClearRef.current = true;
    if (rendererRef.current?.hasLockedPosts()) {
      rendererRef.current.markAllReadUnlessLocked();
    } else {
      rendererRef.current?.markAllRead();
      onMarkAllRead?.();
    }
    suppressUndoClearRef.current = false;
    setAllMarkedRead(true);
    // Offer undo only when something was actually unread (otherwise nothing to restore).
    if (snapshot.some(p => !p.wasRead)) {
      rendererRef.current?.clearKeepUndo();
      markAllReadUndoRef.current = { posts: snapshot, prevPostList };
      setUndoKind('markAllRead');
    }
  }, [posts, onMarkAllRead, appState.feedEngine]);

  // Restore the snapshot taken before "Mark all read": re-mark posts to their prior read
  // state and bring the post list back (in unread-only mode the list was emptied).
  const performMarkAllReadUndo = useCallback(() => {
    const u = markAllReadUndoRef.current;
    if (!u) return;
    suppressUndoClearRef.current = true;
    for (const p of u.posts) {
      const isRead = appState.feedEngine.isPostRead(p.feedId, p.postIndex);
      if (p.wasRead && !isRead) appState.feedEngine.markPostRead(p.feedId, p.postIndex);
      else if (!p.wasRead && isRead) appState.feedEngine.markPostUnread(p.feedId, p.postIndex);
      rendererRef.current?.updatePostStatus(p.feedId, p.postIndex, 'read', p.wasRead);
    }
    suppressUndoClearRef.current = false;
    markAllReadUndoRef.current = null;
    setUndoKind(null);
    setAllMarkedRead(false);
    onRestorePostList?.(u.prevPostList);
  }, [appState.feedEngine, onRestorePostList]);

  // Keep-All undo lives in PostRenderer (it holds the lock snapshot); it notifies here so
  // the menu entry and Keep-All button stay in sync. Keep-All supersedes a Mark-all-read undo.
  const handleKeepUndoChange = useCallback((available: boolean) => {
    if (available) {
      markAllReadUndoRef.current = null;
      setUndoKind('keepAll');
    } else {
      setUndoKind(k => k == 'keepAll' ? null : k);
    }
  }, []);

  const handleUndo = useCallback(() => {
    if (undoKind == 'keepAll') rendererRef.current?.undoKeepAll();
    else if (undoKind == 'markAllRead') performMarkAllReadUndo();
  }, [undoKind, performMarkAllReadUndo]);

  // The "All caught up" screens that follow a Mark-all-read offer undoing it: a minor
  // button next to Next Feed (replacing "Show all posts") and, where the empty state
  // renders its own header, an icon tool button there.
  const markAllReadUndoActive = undoKind == 'markAllRead';
  const undoBottomButton = (
    <button className="markAllReadBottomButton flowFocussable keepAllUnreadBtn" onClick={handleUndo}>
      <UndoIcon />
      Undo
    </button>
  );
  const undoHeaderButton = markAllReadUndoActive && (
    <button className="actionButton flowFocussable sidewayFocus" title="Undo mark all read" onClick={handleUndo}>
      <UndoIcon />
    </button>
  );

  // A feed-order or feed-view change clears any pending undo (feed changes remount PostView).
  useEffect(() => {
    markAllReadUndoRef.current = null;
    setUndoKind(null);
    rendererRef.current?.clearKeepUndo();
  }, [sortNewestFirst, viewMode]);

  useEffect(() => {
    const unregister = appState.feedEngine.onUnreadChange((feedId) => {
      // External actions (e.g. sidebar "Mark All Read") can mark locked posts
      // as read on the server. Re-assert any locks for this feed so locked
      // posts stay unread.
      const lockedPostNumbers = rendererRef.current?.getLockedPostNumbers(feedId);
      if (lockedPostNumbers?.length) {
        for (const postIndex of lockedPostNumbers) {
          if (appState.feedEngine.isPostRead(feedId, postIndex))
            appState.feedEngine.markPostUnread(feedId, postIndex);
        }
      }
      setUnreadVersion(v => v + 1);
    });
    return unregister;
  }, [appState.feedEngine]);

  useEffect(() => {
    const unregister = appState.feedEngine.onStarChange(() => {
      setSavedVersion(v => v + 1);
    });
    return unregister;
  }, [appState.feedEngine]);

  // Preload the next feed's first posts when the user is at the last post or
  // has just marked everything read, so switching via "Next Feed" feels instant.
  useEffect(() => {
    if (!nextUnreadFeed || nextUnreadFeed.isFolder) return;
    const atLastPost = posts.length >= 2 && currentPostIndex >= posts.length - 1;
    if (!atLastPost && !allMarkedRead) return;
    appState.feedEngine.preloadFeed(nextUnreadFeed.id);
  }, [currentPostIndex, posts.length, allMarkedRead, nextUnreadFeed?.id, nextUnreadFeed?.isFolder, appState.feedEngine]); // eslint-disable-line react-hooks/exhaustive-deps

  // A newsletter shows the "Waiting for your first email" screen only until its
  // very first post arrives. Once any post has ever been received we switch to the
  // normal view (which, when there's nothing unread to show, renders the same
  // caught-up state as other feeds, including the "Show all posts" button).
  const newsletterEverReceived = useMemo(() => {
    if (!isNewsletterFeed || !feedConfigKey) return false;
    const feed = appState.feedEngine.getFeed(feedConfigKey);
    return !!feed && feed.postCount > 0;
  }, [isNewsletterFeed, feedConfigKey, appState.feedEngine, unreadVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const unreadCount = useMemo(() => {
    if (!posts.length || specialListType == 'preview') return 0;
    const readSets = new Map<string, Set<number>>();
    let unread = 0;
    for (const [feedId, postNumber] of posts) {
      let readSet = readSets.get(feedId);
      if (!readSet) {
        readSet = appState.feedEngine.getReadForFeed(feedId);
        readSets.set(feedId, readSet);
      }
      if (!readSet.has(postNumber)) unread++;
    }
    return unread;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, appState.feedEngine, unreadVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whether the current feed/list has no unread posts (drives the header's
  // Mark-all-read → Next-Feed/Home swap). For a real feed we ask the engine directly:
  // the displayed `posts` are emptied after "Mark all read" in unread-only mode, so
  // posts.length can't be trusted here. Special/aggregate lists fall back to the
  // displayed posts (allMarkedRead covers the just-clicked case there).
  const feedAllRead = useMemo(() => {
    if (feedConfigKey && !specialListType) {
      const feed = appState.feedEngine.getFeed(feedConfigKey);
      return !!feed && feed.postCount > 0 && appState.feedEngine.getUnreadCount(feedConfigKey) === 0;
    }
    return posts.length > 0 && (allMarkedRead || unreadCount === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedConfigKey, specialListType, posts.length, allMarkedRead, unreadCount, appState.feedEngine, unreadVersion]);

  // The unread-only view has nothing to show. Normally that means the user is
  // done with the feed — but if the engine still counts unread posts the list
  // just hasn't been (re)built yet (the empty-feed bug; Main's watchdog forces a
  // rebuild + poll). Showing "All caught up" there is plain wrong, so we render
  // a loading state instead. It's bounded by a timeout: if the count never
  // resolves into posts (e.g. the unread posts are older than the newest 1000 we
  // display) we fall back to the caught-up screen, whose "Show all posts" button
  // gets the user out.
  const listEmpty = showUnreadOnly && !showAllTemporarily && unreadCount === 0 && !posts.length;
  const unreadPending = listEmpty && !!feedConfigKey && !specialListType
    && appState.feedEngine.getUnreadCount(feedConfigKey) > 0;
  const [pendingTimedOut, setPendingTimedOut] = useState(false);
  useEffect(() => {
    setPendingTimedOut(false);
    if (!unreadPending) return;
    const timer = setTimeout(() => setPendingTimedOut(true), 10000);
    return () => clearTimeout(timer);
  }, [unreadPending, feedConfigKey, specialListType]);

  const readLaterCount = useMemo(() => {
    return appState.feedEngine.getReadLaterCount();
  }, [appState.feedEngine, savedVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Storage-limit fineprint at the bottom of private feeds and newsletter inboxes.
  // Limits aren't enforced yet; this only makes users aware of them. Free users always
  // see their plan's limits; paid users only a usage notice once the feed holds more
  // than 90% of the allowed messages.
  const storageNote = useMemo(() => {
    if ((!isPrivateFeed && !isNewsletterFeed) || !feedConfigKey || getMode() == 'anon') return undefined;
    const plan = appState.state.currentUser?.userPlan ?? 'free';
    const l = getPlanLimits(plan);
    const [maxAgeDays, maxMessages, maxStorageMb] = isNewsletterFeed
      ? [l.maxInboxAgeDays, l.maxInboxMessages, l.maxInboxStorageMb]
      : [l.maxPrivateFeedAgeDays, l.maxPrivateFeedMessages, l.maxPrivateFeedStorageMb];
    const kind = isNewsletterFeed ? 'mails' : 'posts';
    const holder = isNewsletterFeed ? 'inbox' : 'feed';
    const overall = isNewsletterFeed ? 'across all inboxes' : 'across all private feeds';
    if (plan == 'free')
      return `Free plan: ${kind} are kept for ${maxAgeDays} days, up to ${maxMessages} ${kind} per ${holder} and ${maxStorageMb} MB ${overall}.`;
    const feed = appState.feedEngine.getFeed(feedConfigKey);
    const count = feed?.postCount ?? 0;
    if (count >= maxMessages)
      return `This private ${holder} has reached max capacity (${maxMessages} ${kind}). Older ${kind} will be removed as new ones arrive.`;
    if (count <= maxMessages * 0.9) return undefined;
    return `This ${holder} holds ${count} of ${maxMessages} ${kind} included in your plan (up to ${maxStorageMb} MB ${overall}). Older ${kind} may be removed once the limit is reached.`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivateFeed, isNewsletterFeed, feedConfigKey, appState.state.currentUser?.userPlan, appState.feedEngine, unreadVersion]);

  const newUnreadAvailable = useMemo(() => {
    if (specialListType != 'all-items' || posts.length) return false;
    const fe = appState.feedEngine;
    return hasUnreadInScope(fe.getFeeds().map(f => f.id), fe.getFolders(), id => fe.getUnreadCount(id), unreadFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialListType, posts.length, unreadFolder, appState.feedEngine, unreadVersion]);

  // TEMP DEBUG (remove): PERSISTENCE detector for the empty-feed bug. The empty
  // list appears briefly on every feed switch and normally self-heals within a
  // frame or two — those transients are NOT the bug. We only log when it is
  // still showing 0.9s later while the engine still reports unread>0 (just
  // before Main's watchdog kicks in at 1.2s). If postList repopulates,
  // posts/unreadCount change, this effect re-runs, and the cleanup clears the
  // pending timer. postsLen=0 → Main handed us an empty list; postsLen>0 with
  // unreadCount=0 → list is stale (its posts are all read; new unread posts
  // were never added).
  useEffect(() => {
    const fe = appState.feedEngine;
    const caughtUp = showUnreadOnly && !showAllTemporarily && unreadCount === 0 && !posts.length;
    const engineUnread = feedConfigKey ? fe.getUnreadCount(feedConfigKey) : -1;
    if (!(caughtUp && feedConfigKey && engineUnread > 0)) return;
    const timer = setTimeout(() => {
      // Reaching here means the empty state persisted (no heal re-render cleared
      // us) — and the engine still believes there are unread posts.
      if (fe.getUnreadCount(feedConfigKey) <= 0) return;
      const feed = fe.getFeed(feedConfigKey);
      console.warn('[EMPTY-FEED-BUG] STUCK: empty unread list >0.9s while engine reports unread', {
        feedConfigKey,
        postsLen: posts.length,
        postViewUnreadCount: unreadCount,
        engineUnread: fe.getUnreadCount(feedConfigKey),
        barePostCount: feed?.postCount,
        newestRead: fe.getFeedMarker(feedConfigKey)?.newestRead,
        markerUnread: fe.getFeedMarker(feedConfigKey)?.unreadCount,
        readSetSize: fe.getReadForFeed(feedConfigKey).size,
        showUnreadOnly, showAllTemporarily,
        firstPosts: posts.slice(0, 3),
        lastPosts: posts.slice(-3),
        engineState: fe.getState(),
      });
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, unreadCount, showUnreadOnly, showAllTemporarily, feedConfigKey, specialListType, unreadVersion, appState.feedEngine]);

  // Reset allMarkedRead when posts change (e.g. re-enable button after navigating)
  useEffect(() => { setAllMarkedRead(false); }, [posts]);
  // Reset show-all when the feed changes (not when posts rebuild within the same feed)
  useEffect(() => { setShowAllTemporarily(false); }, [feedConfigKey, specialListType]);
  useEffect(() => { setShowScrollNav(false); scrollNavShownRef.current = false; setScrollNavVisible((appState.state.userPreferences.postNav ?? 'autohide') != 'autohide'); }, [feedConfigKey]);

  // Autohide: wheel/touch show/hide, keyboard always hides
  useEffect(() => {
    if ((appState.state.userPreferences.postNav ?? 'autohide') != 'autohide') return;
    const el = postViewRef.current;
    if (!el) return;
    let lastTouchY = 0;
    const onWheel = (ev: WheelEvent) => {
      if (ev.deltaY < 0) setScrollNavVisible(true);
      else if (ev.deltaY > 0) setScrollNavVisible(false);
    };
    const onTouchStart = (ev: TouchEvent) => { lastTouchY = ev.touches[0].clientY; };
    const onTouchMove = (ev: TouchEvent) => {
      const y = ev.touches[0].clientY;
      if (y > lastTouchY + 3) setScrollNavVisible(true);   // finger moving down = scrolling up
      else if (y < lastTouchY - 3) setScrollNavVisible(false);
      lastTouchY = y;
    };
    const onKey = (ev: KeyboardEvent) => {
      if (['ArrowDown', 'ArrowUp', ' ', 'j', 'k', 'PageDown', 'PageUp'].includes(ev.key)) setScrollNavVisible(false);
    };
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('keydown', onKey);
    };
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node))
        setShowMarkReadDropdown(false);
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node))
        setShowMobileMenu(false);
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node))
        setShowOptionsMenu(false);
    };

    if (showMarkReadDropdown || showMobileMenu || showOptionsMenu)
      document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMarkReadDropdown, showMobileMenu, showOptionsMenu]);

  // Auto-focus first menu item when opened by keyboard
  useEffect(() => {
    const menu = showOptionsMenu ? optionsMenuRef.current?.querySelector('.optionsMenuDropdown')
      : showMobileMenu ? mobileMenuRef.current?.querySelector('.mobileMenuDropdown')
      : showMarkReadDropdown ? dropdownRef.current?.querySelector('.dropdownMenu')
      : null;
    if (!menu) return;
    // Only auto-focus if in keyboard mode (visibleFocus class present)
    if (!postViewRef.current?.classList.contains('visibleFocus')) return;
    const first = menu.querySelector('.flowFocussable:not(:disabled)') as HTMLElement | null;
    if (first) requestAnimationFrame(() => first.focus());
  }, [showOptionsMenu, showMobileMenu, showMarkReadDropdown]);

  const handleShowMeHint = useCallback(() => {
    const fromEl = showMeHintRef.current;
    const imgWrapper = postViewRef.current?.querySelector('.imgOnlyWrapper');
    const img = imgWrapper?.querySelector('img');
    if (!fromEl || !img || !imgWrapper) return;

    const fromRect = fromEl.getBoundingClientRect();
    const toRect = img.getBoundingClientRect();
    const sx = fromRect.left + fromRect.width / 2;
    const sy = fromRect.top + fromRect.height / 2;
    const ex = toRect.left + toRect.width / 2;
    const ey = toRect.top + toRect.height / 2;

    const nonce = Date.now();
    setHintGuide({ x: sx, y: sy, dx: ex - sx, dy: ey - sy, nonce, phase: 'travel' });
    setTimeout(() => {
      setHintGuide({ x: ex, y: ey, dx: 0, dy: 0, nonce, phase: 'click' });
    }, 720);
    setTimeout(() => {
      (imgWrapper as HTMLElement).classList.add('imgOnlyCaptionVisible');
      setHintGuide(null);
    }, 1920);
  }, []);

  const handleShowAllPosts = useCallback(() => {
    setShowAllTemporarily(true);
    onShowAll?.();
  }, [onShowAll]);

  // Header twin of the "Show all posts" button at the end of the post list, so revealing the
  // read posts doesn't require scrolling down to it first. Offered under the same conditions
  // as that bottom button (unread-only view, not the all-items list, and no "Show more"
  // occupying that slot); with no posts at all the caught-up screen has its own button.
  // A paused feed always lists all its posts and has nothing to keep unread or skip.
  const feedPaused = !!feedConfigKey && !specialListType && appState.feedEngine.isFeedPaused(feedConfigKey);
  const headerShowAll = onShowAll && posts.length > 0 && showUnreadOnly && !showAllTemporarily
    && !onLoadMore && specialListType != 'all-items' && !feedPaused ? handleShowAllPosts : undefined;

  // Navigating to the next/prev unread feed via in-PostView controls (button or
  // long-press) should mark all unlocked posts as read. Sidebar feed switching
  // (handled outside this component) keeps them unread.
  const handleNavigateToNextFeed = useCallback((feedId: string) => {
    rendererRef.current?.markAllReadUnlessLocked();
    onNavigateToFeed?.(feedId);
  }, [onNavigateToFeed]);

  // Header / end-of-feed "Next Feed" and "Home" buttons. Only auto-mark when the feed
  // fits the viewport (can't scroll): the user has seen every post but the scroll-driven
  // auto-mark never fired. Scrollable feeds are left as-is (the user may not have read
  // everything).
  const navigateAndMaybeMarkRead = useCallback((feedId: string) => {
    const sc = postViewRef.current?.querySelector('.postsContainer') as HTMLElement | null;
    const unscrollable = !sc || sc.scrollHeight <= sc.clientHeight + 1;
    if (unscrollable)
      rendererRef.current?.markAllReadUnlessLocked();
    onNavigateToFeed?.(feedId);
  }, [onNavigateToFeed]);

  // "Skip feed": keep every post unread (like the Keep-All button), then move on to the
  // next unread feed — or Home when there is none — exactly as the Next Feed button would.
  const handleSkipFeed = useCallback(() => {
    rendererRef.current?.keepAllUnread();
    onKeepAllUnread?.();
    onNavigateToFeed?.(nextUnreadFeed?.id ?? 'home');
  }, [onKeepAllUnread, onNavigateToFeed, nextUnreadFeed]);

  const isReadLaterList = specialListType == 'read-later';
  const hasReadPostsInCurrentList = useMemo(() => {
    if (!isReadLaterList) return false;
    for (const [feedId, postNumber] of posts) {
      if (appState.feedEngine.isPostRead(feedId, postNumber)) return true;
    }
    return false;
  }, [isReadLaterList, posts, appState.feedEngine, unreadVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveAllReadFromReadLater = useCallback(() => {
    if (!isReadLaterList) return;
    for (const [feedId, postNumber] of posts) {
      if (!appState.feedEngine.isPostRead(feedId, postNumber)) continue;
      appState.feedEngine.unmarkReadLater(feedId, postNumber);
    }
  }, [isReadLaterList, posts, appState.feedEngine]);

  const handleHintButtonClick = useCallback(() => {
    setShowHintToast(true);
    if (hintToastTimerRef.current) clearTimeout(hintToastTimerRef.current);
    hintToastTimerRef.current = setTimeout(() => {
      setShowHintToast(false);
      hintToastTimerRef.current = null;
    }, 2800);
  }, []);

  useEffect(() => {
    return () => {
      if (hintToastTimerRef.current) {
        clearTimeout(hintToastTimerRef.current);
        hintToastTimerRef.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    const element = postViewRef.current;
    if (!element || typeof ResizeObserver == 'undefined') return;

    const updateWidth = () => {
      const width = element.getBoundingClientRect().width;
      lastMeasuredPostViewWidth = width;
      setPostViewWidth(width);
    };

    updateWidth();
    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = postViewRef.current?.querySelector('.postsContainer');
    if (!container) return;
    const check = () => {
      const scrollTop = container.scrollTop;
      setScrolledToBottom(container.scrollHeight - scrollTop - container.clientHeight < 8);
      setScrolledToTop(scrollTop < 8);

      // Floating post navigation (only when footer is not visible)
      const postNavPref = appState.state.userPreferences.postNav ?? 'autohide';
      if (postNavPref != 'never' && !isGlobalFooterVisible()) {
        if (!scrollNavShownRef.current) {
          scrollNavShownRef.current = true;
          setShowScrollNav(true);
        }
      } else if (scrollNavShownRef.current) {
        scrollNavShownRef.current = false;
        setShowScrollNav(false);
      }
    };
    check();
    container.addEventListener('scroll', check, { passive: true });
    const observer = new ResizeObserver(check);
    observer.observe(container);
    // Also observe children resizing (e.g. post content loading, images)
    for (const child of Array.from(container.children))
      observer.observe(child);
    const mutObserver = new MutationObserver(() => {
      for (const child of Array.from(container.children))
        observer.observe(child);
      check();
    });
    mutObserver.observe(container, { childList: true });
    return () => { container.removeEventListener('scroll', check); observer.disconnect(); mutObserver.disconnect(); };
  });

  // The on-screen nav buttons (footer arrows, side gutters, floating ScrollNavBtn)
  // delegate to the same navNext/navPrev handlers the keyboard (j/k/n/p) uses, so
  // both behave identically. Those handlers resolve the current post from the live
  // visible-post anchor (not the stale currentPostIndex prop) and own all the
  // boundary handling (list mode, snap-to-top, end-of-list focus).
  const handlePrevPostOrScroll = useCallback(() => {
    onPrevPost?.();
  }, [onPrevPost]);

  const handleNextPostOrScroll = useCallback(() => {
    onNextPost?.();
  }, [onNextPost]);

  // Clicking the empty footer area between the counter and the nav arrows
  // does the same as pressing Space: scroll the post content down ~90%.
  // Driven by PostRenderer's own rAF scroll loop: a native smooth scrollBy on iOS
  // Safari's async scrolling races the renderer's virtualization (spacer/scrollTop
  // writes) and overshoots past the end of the content, hiding the bottom buttons.
  const handleFooterSpaceClick = useCallback(() => {
    if (rendererRef.current) return rendererRef.current.scrollPage(1);
    const sc = postViewRef.current?.querySelector('.postsContainer') as HTMLElement | null;
    if (!sc) return;
    sc.dispatchEvent(new Event('cancelscroll'));
    sc.scrollBy({ top: sc.clientHeight * 0.9, behavior: 'smooth' });
  }, []);

  // Post appearance preferences; postWidth/postFontSize feed the CSS variables on
  // the root element, postWidth also drives the gutter hit-test below.
  const postWidth = appState.state.userPreferences.postWidth ?? 700;
  const postFontSize = appState.state.userPreferences.postFontSize ?? 16;
  const postFontSans = appState.state.userPreferences.postFont == 'sans-serif';

  // Gutter nav needs enough empty space on each side of the text column to be a
  // comfortable click target. It becomes worthwhile once the view is ~100px wider
  // than the text column (≈50px of gutter per side). Scales with the now-configurable
  // postWidth rather than the old fixed 1200px threshold.
  const gutterSideWidth = (postViewWidth - postWidth) / 2;
  const wideGutterNav = postViewWidth >= postWidth + 100 && viewMode != 'list' && (appState.state.userPreferences.postNav ?? 'autohide') != 'never';
  // List rows span the whole pane, so list mode carves explicit gutters instead:
  // max(50px, 5% of the pane) per side (keep in sync with the max() in PostView.css),
  // only on panes wide enough that giving up ~100px doesn't cramp the list.
  const listGutterNav = viewMode == 'list' && postViewWidth >= 1000 && (appState.state.userPreferences.postNav ?? 'autohide') != 'never';
  const listGutterWidth = Math.max(50, postViewWidth * 0.05);
  const gutterNav = wideGutterNav || listGutterNav;
  // List navigation handles its own boundaries (like the footer arrows, which are never
  // disabled in list mode), so only the wide gutters disable "prev" at the very top.
  const gutterLeftEnabled = wideGutterNav ? !(currentPostIndex === 0 && scrolledToTop) : listGutterNav;
  const gutterRightEnabled = gutterNav;
  // When at the last post, long-press jumps to the next unread feed if there is
  // one; otherwise falls back to the Home page (mirrors the "Home" button
  // rendered by PostRenderer at end-of-list when no unread feeds are left).
  const moreFallbackTarget = { id: 'home', isMore: true } as const;
  // "At the end of the feed" for the hold-to-next-feed gate. The currentPostIndex prop is
  // the (debounced, reading-line) visible-post anchor, which lags behind a short trailing
  // post: scrolling to the very bottom never makes a small last post the anchor, so the
  // index sticks one short and the long-press target silently disappears even though the
  // renderer has already focused "Next Feed". scrolledToBottom is the definitive end-of-feed
  // signal (full-view only — in list mode there's no single scrolled container to trust),
  // so OR it in to keep hold mode available regardless of viewport height.
  // List mode has no such signal (scrolling to the end-of-feed buttons makes an earlier row the
  // anchor), so there the focused end-of-feed buttons count as the end too — otherwise holding
  // stops working once a press has moved focus to "Next Feed".
  const atEndOfFeed = currentPostIndex >= posts.length - 1 || (viewMode != 'list' && scrolledToBottom && posts.length > 0) || endActionFocused;
  // Enabled in unread-only mode and the all-items list (when not temporarily showing all),
  // and always for browse-only feeds (showRead=a), where the last post is simply the end
  // of the feed — there's no unread/all distinction to gate on.
  const longPressNavTarget: { id: string; name?: string; isMore?: boolean; isFolder?: boolean } | undefined =
    atEndOfFeed
    && (showRead == 'a' || (!showAllTemporarily && (showUnreadOnly || specialListType == 'all-items')))
      ? (nextUnreadFeed ?? moreFallbackTarget)
      : undefined;
  const gutterNextFeedTarget = gutterNav ? longPressNavTarget : undefined;

  const [gutterHoverSide, setGutterHoverSide] = useState<'left' | 'right' | null>(() => lastGutterHoverSide);
  const applyGutterHoverSide = useCallback((side: 'left' | 'right' | null) => {
    lastGutterHoverSide = side;
    setGutterHoverSide(prev => prev == side ? prev : side);
  }, []);

  const resolveGutterSide = useCallback((e: React.MouseEvent): 'left' | 'right' | null => {
    if (!gutterNav) return null;
    const target = e.target as HTMLElement;
    if (target.closest('.postCard') || target.closest('.postItem.listMode') || target.closest('[data-action]') || target.closest('a, button')) return null;
    const container = postViewRef.current?.querySelector('.postsContainer') as HTMLElement | null;
    if (!container || !container.contains(target)) return null;
    if (!container.querySelector('.postItem')) return null;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const center = rect.width / 2;
    if (listGutterNav) {
      // List mode: the gutters are the container's own padding strips.
      const gw = Math.max(50, rect.width * 0.05);
      if (x >= gw && x <= rect.width - gw) return null;
    } else if (Math.abs(x - center) < postWidth / 2) {
      // Only treat as a gutter hover when the pointer is outside the central post
      // column (matches the post-card max-width used by the CSS gutters).
      return null;
    }
    const side = x < center ? 'left' : 'right';
    if (side == 'left' && !gutterLeftEnabled) return null;
    if (side == 'right' && !gutterRightEnabled) return null;
    return side;
  }, [gutterNav, listGutterNav, gutterLeftEnabled, gutterRightEnabled, postWidth]);

  const [gutterLongPressFilling, setGutterLongPressFilling] = useState(false);
  const gutterLongPressFiredRef = useRef(false);
  const gutterStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gutterFireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearGutterLongPressTimers = useCallback(() => {
    if (gutterStartTimerRef.current) { clearTimeout(gutterStartTimerRef.current); gutterStartTimerRef.current = null; }
    if (gutterFireTimerRef.current) { clearTimeout(gutterFireTimerRef.current); gutterFireTimerRef.current = null; }
  }, []);

  const cancelGutterLongPress = useCallback(() => {
    clearGutterLongPressTimers();
    setGutterLongPressFilling(false);
  }, [clearGutterLongPressTimers]);

  useEffect(() => clearGutterLongPressTimers, [clearGutterLongPressTimers]);

  const longPressDuration = Math.min(5000, Math.max(400, appState.state.userPreferences.longPressDuration ?? 1000));

  const handleGutterPointerDown = useCallback((e: React.PointerEvent) => {
    if (!gutterNextFeedTarget) return;
    if (resolveGutterSide(e) != 'right') return;
    gutterLongPressFiredRef.current = false;
    clearGutterLongPressTimers();
    setGutterLongPressFilling(false);
    gutterStartTimerRef.current = setTimeout(() => setGutterLongPressFilling(true), longPressDuration / 4);
    gutterFireTimerRef.current = setTimeout(() => {
      gutterLongPressFiredRef.current = true;
      setGutterLongPressFilling(false);
      handleNavigateToNextFeed(gutterNextFeedTarget.id);
    }, longPressDuration);
  }, [gutterNextFeedTarget, resolveGutterSide, clearGutterLongPressTimers, handleNavigateToNextFeed, longPressDuration]);

  const handleGutterMouseDown = useCallback((e: React.MouseEvent) => {
    // Rapid clicks in the gutter count as double/triple clicks and would start
    // the browser's native word/paragraph selection on the adjacent post text.
    if (e.detail > 1 && resolveGutterSide(e)) e.preventDefault();
  }, [resolveGutterSide]);

  const handleGutterClick = useCallback((e: React.MouseEvent) => {
    if (gutterLongPressFiredRef.current) {
      gutterLongPressFiredRef.current = false;
      return;
    }
    const side = resolveGutterSide(e);
    if (!side) return;
    if (side == 'left') handlePrevPostOrScroll();
    else handleNextPostOrScroll();
  }, [resolveGutterSide, handlePrevPostOrScroll, handleNextPostOrScroll]);

  const handleGutterMouseMove = useCallback((e: React.MouseEvent) => {
    applyGutterHoverSide(resolveGutterSide(e));
  }, [resolveGutterSide, applyGutterHoverSide]);

  const handleGutterMouseLeave = useCallback(() => {
    applyGutterHoverSide(null);
    cancelGutterLongPress();
  }, [applyGutterHoverSide, cancelGutterLongPress]);

  useEffect(() => {
    if (!gutterNav
      || (gutterHoverSide == 'left' && !gutterLeftEnabled)
      || (gutterHoverSide == 'right' && !gutterRightEnabled))
      applyGutterHoverSide(null);
  }, [gutterNav, gutterLeftEnabled, gutterRightEnabled, gutterHoverSide, applyGutterHoverSide]);


  const handleShare = useCallback((feedId: string, postIndex: number, postTitle: string, originalLink: string, x: number, y: number) => {
    // Private feeds: skip context menu, open original post directly
    if (isPrivateFeed && originalLink) {
      window.open(originalLink, '_blank', 'noopener');
      return;
    }
    setShareMenu({
      feedId,
      postIndex,
      postTitle,
      originalLink,
      feedUrl: `https://feeds.downread.com/a/feed/${appState.feedEngine.getFeed(feedId)?.friendlyId || feedId}`,
      position: { x, y },
    });
  }, [appState.feedEngine, isPrivateFeed]);

  // Map PostRenderer's onMark to PostView's toggle callbacks
  // PostRenderer sends value=NEW state, toggle callbacks expect value=OLD state
  const handleMark = useCallback((feedId: string, postIndex: number, type: 'star' | 'readLater' | 'read', value: boolean) => {
    // An individual read/lock change (click or scroll) invalidates a pending Mark-all-read
    // undo. Keep-All undo is invalidated inside PostRenderer, which notifies via onKeepUndoChange.
    if (type == 'read' && !suppressUndoClearRef.current && markAllReadUndoRef.current) {
      markAllReadUndoRef.current = null;
      setUndoKind(k => k == 'markAllRead' ? null : k);
    }
    if (type == 'star') onToggleStar?.(feedId, postIndex, !value);
    else if (type == 'readLater') onToggleReadLater?.(feedId, postIndex, !value);
    else if (type == 'read') onToggleRead?.(feedId, postIndex, !value);
  }, [onToggleStar, onToggleReadLater, onToggleRead]);

  // Walk posts newest-first, stop at the first post older than `days`, then mark
  // it and all lower indices as read. Server has no time-based marking; we do it client-side.
  const handleMarkOlderThanRead = useCallback(async (days: number) => {
    if (!feedConfigKey || specialListType) return;
    const feed = appState.feedEngine.getFeed(feedConfigKey);
    if (!feed) return;
    const postCount = feed.postCount;
    if (!postCount) return;
    const feedInfo = { id: feed.id, postCount, passphrase: feed.passphrase, cdn: feed.cdn, syncInstance: feed.syncInstance };
    const label = days == 1 ? 'one day' : days == 7 ? 'one week' : days == 30 ? 'one month' : `${days} days`;

    presentMarkProgress({
      message: `Marking posts older than ${label} as read\u2026`,
      successMessage: '',
      timeout: 15000,
      successDuration: 1000,
    });

    // Only the most-recent 999 posts are visible to the user; don't walk or mark beyond that.
    const MAX_MARK = 999;
    const minMarkIndex = Math.max(0, postCount - MAX_MARK);
    const cutoffMs = Date.now() - days * 24 * 60 * 60 * 1000;
    let cutoffIndex = -1;
    for (let i = postCount - 1; i >= minMarkIndex; i--) {
      try {
        const post = await getSinglePost(feedInfo, i);
        if (post.pubDate && new Date(post.pubDate).getTime() < cutoffMs) {
          cutoffIndex = i;
          break;
        }
      } catch {
        break;
      }
    }

    if (cutoffIndex < 0) {
      dismissMarkProgress(true, undefined, `No posts older than ${label}`);
      return;
    }
    const readSet = appState.feedEngine.getReadForFeed(feedConfigKey);
    let newlyRead = 0;
    for (let i = minMarkIndex; i <= cutoffIndex; i++)
      if (!readSet.has(i)) newlyRead++;
    appState.feedEngine.markPostsRead(feedConfigKey, [[minMarkIndex, cutoffIndex]]);
    onMarkOlderThanRead?.(days);
    dismissMarkProgress(true, undefined, newlyRead == 0
      ? `No posts older than ${label}`
      : `Marked ${newlyRead} ${newlyRead == 1 ? 'post' : 'posts'} as read`);
  }, [feedConfigKey, specialListType, appState.feedEngine, onMarkOlderThanRead, presentMarkProgress, dismissMarkProgress]);

  // Whether the prominent header "Mark all read" / "Next Feed" group fits, vs. collapsing
  // into the ⋮ options menu. Gated on the PostView pane width (not the window): in
  // double-column mode the headline list eats horizontal space, so at medium/tablet
  // resolutions (~768px) the pane is ~500px — comfortably enough for the ~167px button —
  // yet a 600px gate hid it there while single/list (full-width pane) kept it. 430px shows
  // it in all three view modes at medium res; single-column panes are always near
  // full-width so this only affects the double-column case. Keep in sync with the
  // @container query in PostView.css that hides .markReadGroup.
  const showMarkAllHeaderActions = postViewWidth >= 430;
  const showSmallHeaderActions = postViewWidth >= 390;

  // Scroll to post when scrollToPost changes
  useEffect(() => {
    if (!scrollToPost) return;
    rendererRef.current?.scrollToPost(scrollToPost[0], scrollToPost[1]);
  }, [scrollToPost]);

  if (isNavigatingAway) {
    return (
      <div className="postView flowForm" ref={postViewRef}>
        <div className="postViewHeader">
          {renderEmptyStateHeaderLeft(feedName || 'Unread Posts', true)}
        </div>
        <div className="postsContainer">
          <div className="allReadState">
            <Spinner spinning />
          </div>
        </div>
      </div>
    );
  }

  if (!posts.length && !feedName) {
    return (
      <div className="postView empty">
        <div className="emptyState">
          <div className="emptyIcon">📖</div>
          <h2>Select a feed to read</h2>
          <p>Choose a feed from the sidebar to view all its posts here.</p>
        </div>
      </div>
    );
  }

  if (!posts.length && feedLoadingPosts) {
    return (
      <div className="postView flowForm" ref={postViewRef}>
        <div className="postViewHeader">
          {renderEmptyStateHeaderLeft(feedName, true)}
        </div>
        <div className="postsContainer">
          <div className="allReadState">
            <Spinner spinning />
            <p className="allReadText">Loading posts for this feed…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!posts.length && isNewsletterFeed && !newsletterEverReceived) {
    return (
      <div className="postView flowForm" ref={postViewRef}>
        <div className="postViewHeader">
          {renderEmptyStateHeaderLeft(feedName, true)}
        </div>
        <div className="postsContainer">
          <div className="nlWaiting">
            <div className="nlWaitingIcon"><MailIcon size={36} color="currentColor" /></div>
            <h2 className="nlWaitingTitle">Waiting for your first email</h2>
            <p className="nlWaitingText">
              This inbox is ready. Subscribe to a newsletter with the address below - or forward one you
              already receive - and it'll show up here as a post.
            </p>
            {newsletterEmail && (
              <div className="nlWaitingEmailBox">
                <span className="nlWaitingEmail" title={newsletterEmail}>{newsletterEmail}</span>
                <TextButton label={copiedNewsletterEmail ? 'Copied!' : 'Copy'} type="major" size="small" focussable="flow" onClick={copyNewsletterEmail} />
              </div>
            )}
            <p className="nlWaitingHint">The first email can take a few minutes to arrive.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!posts.length && specialListType && specialListType != 'preview') {
    const isStarred = specialListType == 'starred';
    const isAllItems = specialListType == 'all-items';
    const titleFallback = isAllItems ? 'Unread Posts' : isStarred ? 'Starred' : 'Read Later';
    return (
      <div className="postView flowForm" ref={postViewRef}>
        <div className="postViewHeader">
          {renderEmptyStateHeaderLeft(feedName || titleFallback, false)}
          <div className="postViewActions">
            {undoHeaderButton}
          </div>
        </div>
        <div className="postsContainer">
          {isAllItems ? (
            <div className="allReadState">
              {loading ? (<>
                <Spinner spinning />
                <p className="allReadText">Loading unread posts...</p>
              </>) : (<>
                <AllReadIllustration size={170} />
                {newUnreadAvailable ? (<>
                  <p className="allReadText">New unread posts have arrived.</p>
                  <button className="markAllReadBottomButton flowFocussable" onClick={onRefresh}>
                    Load new posts
                  </button>
                </>) : nextUnreadFeed ? (<>
                  {/* Folder unread view with other unread feeds/folders left: offer the
                      next stop instead of the misleading all-feeds caught-up message. */}
                  <p className="allReadText">{markAllReadUndoActive ? 'All posts have been marked as read.' : 'All caught up! No unread posts in this folder.'}</p>
                  <div className="endOfUnreadActions">
                    {markAllReadUndoActive && undoBottomButton}
                    <div className="endOfUnreadNextFeed">
                      <button className="markAllReadBottomButton flowFocussable" onClick={() => onNavigateToFeed?.(nextUnreadFeed.id)}>
                        <ArrowRightIcon size={14} />
                        <span style={{ marginLeft: 6 }}>{nextUnreadFeed.isFolder ? 'Next Folder' : 'Next Feed'}</span>
                      </button>
                      <span className="endOfUnreadNextFeedName">{nextUnreadFeed.name}</span>
                    </div>
                  </div>
                </>) : (<>
                  {markAllReadUndoActive ? (<>
                    <h2 className="caughtUpHeadline">All posts marked as read</h2>
                    <p className="caughtUpText">That cleared every unread post in your feeds. New posts will show up here as they arrive, or explore something new in the meantime.</p>
                  </>) : (<>
                    <h2 className="caughtUpHeadline">You're all caught up!</h2>
                    <p className="caughtUpText">There are no unread posts in any of your feeds right now. Check back later, or explore something new in the meantime.</p>
                  </>)}
                  {markAllReadUndoActive && undoBottomButton}
                  {readLaterCount > 0 && (
                    <button className="caughtUpLink flowFocussable" onClick={() => onNavigateToFeed?.('read-later')}>
                      {readLaterCount} saved in Read Later →
                    </button>
                  )}
                  <div className="caughtUpPromoGrid">
                    <button className="caughtUpCard flowFocussable" onClick={() => onNavigateToFeed?.('add')}>
                      <span className="caughtUpCardIcon"><PlusIcon size={26} /></span>
                      <span className="caughtUpCardText">
                        <span className="caughtUpCardTitle">Find more feeds</span>
                        <span className="caughtUpCardDesc">Search for any site, or paste a feed URL.</span>
                      </span>
                    </button>
                  </div>
                </>)}
              </>)}
            </div>
          ) : (
            <div className="allReadState listEmptyState">
              <SpecialListArt type={specialListType as 'starred' | 'read-later'} />
              <p className="allReadText">
                {isStarred ? 'No starred posts yet.' : 'No posts saved for later yet.'}
              </p>
              <p className="listEmptyHintText">
                {isStarred
                  ? 'Press the star button on any post to add it to Starred.'
                  : 'Press the Read later button on any post to add it here.'}
              </p>
              <button className={`actionButton listHintButton ${isStarred ? 'starred' : 'readLater'} flowFocussable`} type="button" onClick={handleHintButtonClick}>
                <span style={isStarred ? undefined : {lineHeight: 1}}>{isStarred ? '★' : '◷'}</span>
                <span className="actionButtonLabel">{isStarred ? 'Star' : 'Read later'}</span>
              </button>
              {showHintToast && (
                <div className="listHintToast">
                  You got it! That's what the button looks like. Now find it next to a post to save it. 😀
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`postView flowForm${postFontSans ? ' postFontSans' : ''}${wideGutterNav ? ' wideGutterNav' : ''}${listGutterNav ? ' listGutterNav' : ''}${gutterLeftEnabled ? ' gutterLeftEnabled' : ''}${gutterRightEnabled ? ' gutterRightEnabled' : ''}${gutterHoverSide ? ` gutterHover-${gutterHoverSide}` : ''}${listHeader && scrolledToTop ? ' previewAtTop' : ''}`} style={{ '--pv-font-size': `${postFontSize}px`, '--post-width': `${postWidth}px` } as React.CSSProperties} ref={postViewRef} onClick={handleGutterClick} onMouseDown={handleGutterMouseDown} onMouseMove={handleGutterMouseMove} onMouseLeave={handleGutterMouseLeave} onPointerDown={handleGutterPointerDown} onPointerUp={cancelGutterLongPress} onPointerCancel={cancelGutterLongPress} onFocus={handleEndActionFocus} onBlur={handleEndActionBlur}>
      {hintGuide && (hintGuide.phase == 'travel'
        ? <ShowMeWhereGuide guide={hintGuide} />
        : <div key={hintGuide.nonce + '-click'} className="showMeClickEffect" style={{ left: hintGuide.x, top: hintGuide.y }} aria-hidden="true">
            <div className="showMeClickBall" />
            <div className="showMeClickLabel">click!</div>
          </div>
      )}
      {savedLimitToast && (
        <div className="savedLimitToast" role="alert">
          {savedLimitToast == 'star' ? 'Starred is full' : 'Read Later is full'} — {getMode() == 'anon'
            ? <>without an account you can save up to {MAX_ANON_SAVED_POSTS} posts per list. <a href="/signup">Sign up</a> to save more.</>
            : <>you can save up to {MAX_SAVED_POSTS} posts per list. Remove some saved posts to make room.</>}
        </div>
      )}
      <HelpPopup
        show={showPrivateInfo}
        title="Private Feed"
        onClose={() => setShowPrivateInfo(false)}
        closable={true}
      >
        <div>
          This is a private feed. It will not be published or shown to other users.
        </div>
        <div style={{ marginTop: '12px' }}>
          Only users who know the exact feed URL can add it to their own account.
          Private feeds are intended for personal subscriptions such as paid newsletters
          or feeds with personalized content.
        </div>
      </HelpPopup>
      <NewsletterInfoPopup
        show={showNewsletterInfo}
        email={(newsletterEmail ?? '').replace(/^mailto:/i, '')}
        feedTitle={feedName}
        closeFocus="pvNlBadge"
        onClose={() => setShowNewsletterInfo(false)}
      />
      <FeedIssuePopup
        show={showIssueInfo}
        fetchErrorCount={feedFetchErrorCount}
        fetchFailingSinceAt={feedFailingSinceAt}
        feedTitle={feedName}
        closeFocus="pvIssueBadge"
        onClose={() => setShowIssueInfo(false)}
      />
      {specialListType == 'preview' ? (
        <div className="postViewHeader">
          <div className="previewHeaderLeft">
            {onBack && (
              <button
                className="pageHeaderBack flowFocussable sidewayFocus"
                tabIndex={0}
                title="Back"
                onClick={onBack}
              >
                <ChevronLeftIcon size={22} />
              </button>
            )}
            {renderEmptyStateHeaderLeft(feedName, false)}
          </div>
          <div className="postViewActions">
            {/* Like a followed feed's ⋮ menu, in the same order, with what applies to a feed you don't
                follow, plus Follow on top. The layout choice only lasts for the visit; text appearance is a global preference. */}
            <div className="optionsMenuContainer" ref={optionsMenuRef}>
              <button ref={optionsMenuButtonRef} className="actionButton flowFocussable sidewayFocus" onClick={() => setShowOptionsMenu(!showOptionsMenu)} title="Options">
                ⋮
              </button>
              {showOptionsMenu && (
                <div className="optionsMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setShowOptionsMenu(false))} onBlur={menuBlur(() => setShowOptionsMenu(false))}>
                  {onFollow && <>
                  <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { setShowOptionsMenu(false); onFollow(); }}>
                    <span className="optionsMenuIcon"><PlusIcon size={16} /></span>
                    <span className="optionsMenuLabel">Follow</span>
                  </button>
                  <div className="optionsMenuDivider"></div>
                  </>}
                  {onSetViewMode && <>
                  <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('list'); setShowOptionsMenu(false); }}>
                    <span className="optionsMenuIcon"><ListIcon size={16} /></span>
                    <span className="optionsMenuLabel">List</span>
                    {viewMode == 'list' && <span className="optionsMenuCheck">✓</span>}
                  </button>
                  <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('single'); setShowOptionsMenu(false); }}>
                    <span className="optionsMenuIcon"><SingleColumnIcon size={16} /></span>
                    <span className="optionsMenuLabel">Single column</span>
                    {viewMode != 'list' && <span className="optionsMenuCheck">✓</span>}
                  </button>
                  <div className="optionsMenuDivider"></div>
                  </>}
                  <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { openPostAppearance(); setShowOptionsMenu(false); }}>
                    <span className="optionsMenuIcon postAppearanceAa">Aa</span>
                    <span className="optionsMenuLabel">Text appearance…</span>
                  </button>
                  {previewFeedId && <div className="optionsMenuDivider"></div>}
                  {previewFeedId && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { report({ feedId: previewFeedId, title: feedName }); setShowOptionsMenu(false); }}>
                    <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
                    <span className="optionsMenuLabel">Report this feed</span>
                  </button>}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : <PostViewHeader
        feedName={feedName}
        feedIcon={feedIcon}
        privateLockIcon={privateLockIcon}
        feedNameClass={feedNameClass}
        feedNameClickHandler={feedNameClickHandler}
        feedTileShown={!!listHeader}
        isMobile={isMobile}
        showExpandButton={showExpandButton}
        onExpand={onExpand}
        viewMode={viewMode}
        onCycleViewMode={onCycleViewMode}
        onSetViewMode={onSetViewMode}
        sortNewestFirst={sortNewestFirst}
        onSortChange={onSortChange}
        onShowAll={headerShowAll}
        onFeedSetup={feedSetupHandler}
        allMarkedRead={allMarkedRead}
        feedAllRead={feedAllRead || allReadOrLocked}
        nextUnreadFeed={nextUnreadFeed}
        onNavigateToFeed={navigateAndMaybeMarkRead}
        onMarkAllReadClick={handleMarkAllReadClick}
        onMarkOlderThanRead={handleMarkOlderThanRead}
        rendererRef={rendererRef}
        showMarkAllHeaderActions={showMarkAllHeaderActions}
        showSmallHeaderActions={showSmallHeaderActions}
        keepAllLocked={allLocked}
        // No posts to display (e.g. the "All caught up" state) → nothing to keep or skip, so
        // the Keep-All menu entry and the Skip-feed button are omitted (both gated on their
        // handler being present).
        onKeepAll={posts.length && !feedPaused ? () => rendererRef.current?.keepAllAction() : undefined}
        onSkipFeed={posts.length && !feedPaused ? handleSkipFeed : undefined}
        onUndo={undoKind ? handleUndo : undefined}
        undoLabel={undoKind == 'keepAll' ? 'Undo Keep All' : 'Undo Mark All Read'}
        markAllReadUndoActive={undoKind == 'markAllRead'}
        isReadLaterList={isReadLaterList}
        hasReadPostsInCurrentList={hasReadPostsInCurrentList}
        onRemoveAllReadFromReadLater={handleRemoveAllReadFromReadLater}
        onLimitUnread={feedConfigKey && !specialListType ? () => openLimitUnread(feedConfigKey) : undefined}
        feedLimits={feedConfigKey && !specialListType ? appState.feedEngine.getFeedLimits(feedConfigKey) : undefined}
        onFilterPosts={canFilterPosts && feedConfigKey && !specialListType ? () => goTo(routerCtx, `/a/filter/${feedConfigKey}`) : undefined}
        filterActive={!!feedConfigKey && !!appState.feedEngine.getFeedFilter(feedConfigKey)}
        onPauseFeed={feedConfigKey && !specialListType ? () => openPauseFeed(feedConfigKey) : undefined}
        pausedUntil={feedConfigKey && !specialListType ? appState.feedEngine.getPausedUntil(feedConfigKey) : undefined}
        onUnfollow={onUnfollow}
        unfollowIsDelete={isNewsletterFeed}
        onReportFeed={feedConfigKey ? () => report({ feedId: feedConfigKey, title: feedName }) : undefined}
        onReportPost={specialListType ? () => {
          const cur = rendererRef.current?.getCurrentVisible() ?? posts[currentPostIndex];
          if (cur) report({ feedId: cur[0], postIndex: cur[1] });
        } : undefined}
        specialListType={specialListType}
        onPostAppearance={openPostAppearance}
        showOptionsMenu={showOptionsMenu}
        setShowOptionsMenu={setShowOptionsMenu}
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        onBack={onBack}
        showMarkReadDropdown={showMarkReadDropdown}
        setShowMarkReadDropdown={setShowMarkReadDropdown}
        dropdownRef={dropdownRef}
        mobileMenuRef={mobileMenuRef}
        optionsMenuRef={optionsMenuRef}
        optionsMenuButtonRef={optionsMenuButtonRef}
      />}

      {showImageHintCard && (
        <div className="feedConfigCard">
          <div className="feedConfigTopRow" style={{gap: '12px'}}>
            <div className="hintIconGlow"><HintIcon size={40} /></div>
            <div className="feedConfigText">
              <p className="feedConfigQuestion" style={{margin: '0 0 4px', fontSize: '16px'}}>Hint</p>
              <p style={{margin: 0}}>Click on an image to reveal the hidden image caption.
                <span ref={showMeHintRef}>
                  {' '}<FocusText className="feedConfigShowMe hintShowMe" focusVisible={false} focussable="flow" onClick={handleShowMeHint}>Show me</FocusText>
                </span>
              </p>
            </div>
            <button className="feedConfigClose flowFocussable" onClick={() => {
              setFirstPostImageOnly(false);
              const prefs = appState.state.userPreferences;
              updatePreferences({ dismissed: (prefs.dismissed || '') + 'x' });
            }} aria-label="Dismiss hint">&times;</button>
          </div>
        </div>
      )}
      {listEmpty && unreadPending && !pendingTimedOut ? (
        <div className="postsContainer">
          <div className="allReadState loadingUnreadState">
            <Spinner spinning />
            <p className="allReadText">Loading unread posts…</p>
          </div>
        </div>
      ) : listEmpty ? (
        <div className="postsContainer">
          {/* The FeedTile stays reachable when nothing is unread (a paused feed always shows it). */}
          {listHeader}
          <div className="allReadState">
            <AllReadIllustration size={170} />
            <p className="allReadText">
              {markAllReadUndoActive
                ? 'All posts have been marked as read.'
                : nextUnreadFeed
                ? 'All caught up! No unread posts in this feed.'
                : 'All caught up! No unread posts in any feed.'}
            </p>
            {nextUnreadFeed ? (
              <div className="endOfUnreadActions">
                {markAllReadUndoActive ? undoBottomButton : (posts.length > 0 || onShowAll) && (
                  <button className="markAllReadBottomButton flowFocussable keepAllUnreadBtn" onClick={handleShowAllPosts}>
                    <ShowAllIcon size={14} />
                    Show all posts
                  </button>
                )}
                <div className="endOfUnreadNextFeed">
                  <button className="markAllReadBottomButton flowFocussable" onClick={() => handleNavigateToNextFeed(nextUnreadFeed.id)}>
                    <ArrowRightIcon size={14} />
                    <span style={{ marginLeft: 6 }}>{nextUnreadFeed.isFolder ? 'Next Folder' : 'Next Feed'}</span>
                  </button>
                  <span className="endOfUnreadNextFeedName">{nextUnreadFeed.name}</span>
                </div>
              </div>
            ) : markAllReadUndoActive ? undoBottomButton : (posts.length > 0 || onShowAll) && (
              <button className="markAllReadBottomButton flowFocussable" onClick={handleShowAllPosts}>
                <ShowAllIcon size={14} />
                <span style={{ marginLeft: 6 }}>Show all posts</span>
              </button>
            )}
            {storageNote && <p className="feedStorageNote">{storageNote}</p>}
          </div>
        </div>
      ) : (
        <PostRenderer
          // Rebuilt when the feed is paused or resumed: that marks its posts read/unread engine-side,
          // which the renderer's per-post read state wouldn't otherwise pick up.
          key={`${feedConfigKey ?? specialListType ?? 'none'}${feedPaused ? ':paused' : ''}`}
          ref={rendererRef}
          posts={posts}
          startAt={currentPostIndex}
          onAllRead={onMarkAllRead || (() => {})}
          onMark={handleMark}
          onClick={() => {}}
          onVisiblePostChange={onVisiblePostChange || (() => {})}
          showUnreadOnly={showUnreadOnly && !showAllTemporarily}
          onEndOfUnread={onEndOfUnread}
          onShowAll={handleShowAllPosts}
          viewMode={viewMode}
          isSingleFeed={!!feedName && (!specialListType || specialListType == 'preview')}
          userPreferences={appState.state.userPreferences}
          overflowMessage={overflowMessageProp ?? (hasMoreUnread ? 'Showing first 1000 unread posts.' : undefined)}
          onOverflowAction={onMarkAllAndLoadMore}
          onLoadMore={onLoadMore}
          loadMoreLabel={loadMoreLabel}
          loadMoreWithNextFeed={loadMoreWithNextFeed}
          onShare={handleShare}
          onFirstPostImageOnly={setFirstPostImageOnly}
          nextUnreadFeed={nextUnreadFeed}
          onNavigateToFeed={navigateAndMaybeMarkRead}
          onKeepAllUnread={() => { rendererRef.current?.keepAllUnread(); onKeepAllUnread?.(); }}
          onLockedChange={s => { setAllLocked(s.allLocked); setAllReadOrLocked(s.allReadOrLocked); }}
          onKeepUndoChange={handleKeepUndoChange}
          readLaterCount={readLaterCount}
          isUnreadList={specialListType == 'all-items'}
          isSavedList={specialListType == 'starred' || specialListType == 'read-later' || specialListType == 'preview'}
          savedListType={specialListType == 'starred' || specialListType == 'read-later' ? specialListType : undefined}
          topContent={listHeader}
          hideFollowButtons={specialListType == 'preview'}
          hideKeepAll={feedPaused}
          anonSavedListNote={(specialListType == 'starred' || specialListType == 'read-later') && getMode() == 'anon'}
          feedCategory={feedCategory}
          bottomNote={storageNote}
        />
      )}

      {gutterLeftEnabled && (
        <div className="gutterChevron gutterChevronLeft" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </div>
      )}
      {gutterRightEnabled && (
        <div className="gutterChevron gutterChevronRight" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          {gutterNextFeedTarget && (
            <span className="gutterChevronLabel">
              {(listGutterNav ? listGutterWidth : gutterSideWidth) < 250 ? 'Hold' : gutterNextFeedTarget.isMore ? 'Hold for home' : gutterNextFeedTarget.isFolder ? 'Hold for next folder' : 'Hold for next feed'}
              <span
                className="gutterChevronLabelUnderline"
                style={{
                  transform: gutterLongPressFilling ? 'scaleX(1)' : 'scaleX(0)',
                  transition: gutterLongPressFilling ? `transform ${longPressDuration * 0.75}ms linear` : 'none',
                }}
              />
            </span>
          )}
        </div>
      )}

      {showScrollNav && !gutterNav && !(showUnreadOnly && !showAllTemporarily && unreadCount === 0 && !posts.length) && (
        <div className={`scrollNavFloat${(appState.state.userPreferences.postNav ?? 'autohide') == 'autohide' && !scrollNavVisible ? ' scrollNavAutoHidden' : ''}`}>
          {(() => {
            const autohide = (appState.state.userPreferences.postNav ?? 'autohide') == 'autohide';
            const isList = viewMode == 'list';
            const atLastPost = atEndOfFeed;
            const longPressTarget = longPressNavTarget;
            return <>
              <ScrollNavBtn hidden={!autohide && !isList && currentPostIndex === 0 && scrolledToTop} onClick={handlePrevPostOrScroll} title="Previous post">↑</ScrollNavBtn>
              <ScrollNavBtn
                hidden={!autohide && !isList && scrolledToBottom && !longPressTarget}
                onClick={handleNextPostOrScroll}
                title={longPressTarget ? (longPressTarget.isMore ? 'Home' : longPressTarget.isFolder ? 'Next Folder' : 'Next Feed') : atLastPost ? 'Scroll to bottom' : 'Next post'}
                longPressDurationMs={longPressTarget ? longPressDuration : undefined}
                onLongPress={longPressTarget ? () => handleNavigateToNextFeed(longPressTarget.id) : undefined}
                rotate={longPressTarget ? 270 : 0}
              >↓</ScrollNavBtn>
            </>;
          })()}
        </div>
      )}

      {sharePopup}
      {reportPopup}
      {limitUnreadPopup}
      {pauseFeedPopup}
      {postAppearancePopup}

      {shareMenu && (
        <ContextMenu
          items={[
            ...(shareMenu.originalLink ? [{ label: 'Share original post', action: 'original' }] : []),
            { label: 'Share feed', action: 'feed' },
            { label: 'Report this post', action: 'report' },
          ] as ContextMenuItem[]}
          position={shareMenu.position}
          onItemClick={(action) => {
            const m = shareMenu;
            setShareMenu(null);
            if (action == 'original') share(m.postTitle, m.originalLink);
            else if (action == 'feed') share(m.postTitle, m.feedUrl);
            else if (action == 'report') report({ feedId: m.feedId, postIndex: m.postIndex, title: m.postTitle });
          }}
          onClose={() => setShareMenu(null)}
        />
      )}

      <GlobalFooterContent>
          {/* The middle shows the remaining count (when there's room) and, like Space,
              scrolls the post content down when tapped. */}
          <div className="postViewFooterSpace" onClick={handleFooterSpaceClick}>
            {(() => {
              if (scrolledToBottom) return null;
              const uniqueFeedIds = Array.from(new Set(posts.map(([feedId]) => feedId)));
              const lockedCount = uniqueFeedIds.reduce((sum, feedId) => sum + (rendererRef.current?.getLockedPostNumbers(feedId).length ?? 0), 0);
              const remaining = unreadCount - lockedCount;
              if (remaining <= 0) return null;
              return <span className="postViewFooterCount">{remaining >= 1000 ? '999+' : remaining} left</span>;
            })()}
          </div>
          <div className="postViewFooterNav">
            <PostViewNavButton
              onClick={handlePrevPostOrScroll}
              disabled={viewMode == 'list' ? false : (currentPostIndex === 0 && scrolledToTop)}
              title="Previous post"
            >
              ↑
            </PostViewNavButton>
            {(() => {
              const atLastPost = atEndOfFeed;
              const longPressTarget = longPressNavTarget;
              return (
                <PostViewNavButton
                  onClick={handleNextPostOrScroll}
                  disabled={viewMode == 'list' ? false : (scrolledToBottom && !longPressTarget)}
                  title={longPressTarget ? (longPressTarget.isMore ? 'Home' : longPressTarget.isFolder ? 'Next Folder' : 'Next Feed') : atLastPost ? 'Scroll to bottom' : 'Next post'}
                  longPressDurationMs={longPressTarget ? longPressDuration : undefined}
                  onLongPress={longPressTarget ? () => handleNavigateToNextFeed(longPressTarget.id) : undefined}
                  rotate={longPressTarget ? 270 : 0}
                >
                  ↓
                </PostViewNavButton>
              );
            })()}
          </div>
      </GlobalFooterContent>
    </div>
  );
});

export default PostView;
